import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Factory, Plus } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import { apiError, fmtDate, type IndustryTenant } from '../lib/industria'
import {
  ORDER_STATUS_BADGE, ORDER_STATUS_LABEL,
  type OrderRow, type OrderStatus, type RecipeRow,
} from '../lib/producao'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

const STATUSES: OrderStatus[] = [
  'planned', 'released', 'in_progress', 'reported', 'pending_approval', 'closed', 'cancelled',
]

export function IndustriaOpsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-ops', 'edit')

  const [tenantId, setTenantId] = useState('')
  const [status, setStatus] = useState('')
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    tenantId: '', productId: '', qtyPlanned: '', scheduledFor: '', expiryDate: '', notes: '',
  })

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const { data: orders = [], isLoading } = useQuery<OrderRow[]>({
    queryKey: ['producao', 'orders', tenantId, status],
    queryFn: async () => (await api.get('/production-orders', {
      params: { tenantId: tenantId || undefined, status: status || undefined },
    })).data,
  })

  // Só produto com ficha ATIVA entra numa OP — é a versão que ela congela.
  const { data: recipes = [] } = useQuery<RecipeRow[]>({
    queryKey: ['producao', 'recipes', form.tenantId, 'active'],
    queryFn: async () => (await api.get('/product-recipes', {
      params: { tenantId: form.tenantId, status: 'active' },
    })).data,
    enabled: open && !!form.tenantId,
  })

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/production-orders', {
      tenantId: form.tenantId,
      productId: form.productId,
      qtyPlanned: Number(form.qtyPlanned),
      scheduledFor: form.scheduledFor || null,
      expiryDate: form.expiryDate || null,
      notes: form.notes.trim() || null,
    })).data as { id: string; number: string },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['producao', 'orders'] })
      setOpen(false)
      navigate(`/industria/ops/${row.id}`)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível criar a ordem')),
  })

  const blocked = orders.filter(o => o.pendingApprovals > 0).length

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Ordens de produção</h2>
          <p className="text-sm text-gray-500">
            Planejar → liberar (reserva o material e gera o lote) → apontar → encerrar
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setError(null); setOpen(true) }}>
            <Plus className="h-4 w-4" /> Nova ordem
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className="mb-1 block text-sm font-medium text-gray-700">Marca</label>
            <select className={selectClass} value={tenantId} onChange={e => setTenantId(e.target.value)}>
              <option value="">Todas</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="w-52">
            <label className="mb-1 block text-sm font-medium text-gray-700">Situação</label>
            <select className={selectClass} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">Todas</option>
              {STATUSES.map(s => <option key={s} value={s}>{ORDER_STATUS_LABEL[s]}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      {blocked > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {blocked} ordem(ns) com perda acima do teto aguardando decisão — o encerramento delas está travado.
        </div>
      )}

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">{orders.length} ordem(ns)</span>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : orders.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Factory className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">
                Nenhuma ordem — o produto precisa de ficha técnica ativa antes
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Ordem</th>
                    <th className="px-4 py-2.5 text-left font-medium">Produto</th>
                    <th className="px-4 py-2.5 text-left font-medium">Marca</th>
                    <th className="px-4 py-2.5 text-right font-medium">Planejado</th>
                    <th className="px-4 py-2.5 text-right font-medium">Produzido</th>
                    <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                    <th className="px-4 py-2.5 text-left font-medium">Programada</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map(o => (
                    <tr
                      key={o.id}
                      onClick={() => navigate(`/industria/ops/${o.id}`)}
                      className="cursor-pointer hover:bg-blue-50/40"
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-mono font-medium text-gray-900">{o.number}</span>
                        {o.recipeVersion != null && (
                          <span className="block text-xs text-gray-400">ficha v{o.recipeVersion}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{o.productName}</span>
                        <span className="block font-mono text-xs text-gray-400">{o.productSku}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{o.tenantName}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {o.qtyPlanned.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {o.qtyProduced ? o.qtyProduced.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{o.lotNumber ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{fmtDate(o.scheduledFor)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={ORDER_STATUS_BADGE[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge>
                        {o.pendingApprovals > 0 && (
                          <span className="ml-1 text-xs font-semibold text-red-600">
                            {o.pendingApprovals} perda(s)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Nova ordem de produção" open={open} onClose={() => setOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); createMutation.mutate() }}
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Marca</label>
            <select
              className={selectClass}
              value={form.tenantId}
              onChange={e => setForm({ ...form, tenantId: e.target.value, productId: '' })}
              required
            >
              <option value="">Selecione...</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Produto (com ficha ativa)</label>
            <select
              className={selectClass}
              value={form.productId}
              onChange={e => setForm({ ...form, productId: e.target.value })}
              disabled={!form.tenantId}
              required
            >
              <option value="">Selecione...</option>
              {recipes.map(r => (
                <option key={r.id} value={r.productId}>
                  {r.productSku} — {r.productName} (ficha v{r.version})
                </option>
              ))}
            </select>
            {form.tenantId && recipes.length === 0 && (
              <p className="mt-1 text-xs text-amber-700">
                Esta marca não tem nenhuma ficha ativa. Ative uma em Fichas técnicas.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Quantidade"
              type="number"
              min={1}
              value={form.qtyPlanned}
              onChange={e => setForm({ ...form, qtyPlanned: e.target.value })}
              required
            />
            <Input
              label="Programada para"
              type="date"
              value={form.scheduledFor}
              onChange={e => setForm({ ...form, scheduledFor: e.target.value })}
            />
            <Input
              label="Validade"
              type="date"
              value={form.expiryDate}
              onChange={e => setForm({ ...form, expiryDate: e.target.value })}
            />
          </div>

          <Input
            label="Observações"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
          />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={createMutation.isPending} disabled={!form.productId}>
              Criar planejada
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
